import type { AttendanceRepository } from '../repositories';
import type { CurrentUser, DailyAttendanceRecord, AttendanceStats } from '../models';

/**
 * 출결 관리 도메인 서비스
 * 비즈니스 로직과 데이터 조회를 담당
 */
export class AttendanceService {
  constructor(private repository: AttendanceRepository) {}

  /**
   * 현재 이용 중인 사용자 목록 조회
   * 실패 시 폴백 데이터 제공
   */
  async getCurrentUsers(): Promise<CurrentUser[]> {
    try {
      return await this.repository.getCurrentUsers();
    } catch (error) {
      console.error('현재 이용자 조회 실패, 폴백 데이터 사용:', error);
      return this.getFallbackCurrentUsers();
    }
  }

  /**
   * 특정 날짜의 일일 출결 기록 조회
   * 실패 시 폴백 데이터 제공
   */
  async getDailyAttendance(studyroomId: number, date: string): Promise<DailyAttendanceRecord[]> {
    try {
      return await this.repository.getDailyAttendance(studyroomId, date);
    } catch (error) {
      console.error('일일 출결 기록 조회 실패, 폴백 데이터 사용:', error);
      return this.getFallbackDailyRecords(date);
    }
  }

  /**
   * 출결 통계 조회
   * 실패 시 폴백 데이터 제공
   */
  async getStats(): Promise<AttendanceStats> {
    try {
      return await this.repository.getStats();
    } catch (error) {
      console.error('출결 통계 조회 실패, 폴백 데이터 사용:', error);
      return this.getFallbackStats();
    }
  }

  /**
   * 폴백: 개발용 현재 이용자 더미 데이터
   */
  private getFallbackCurrentUsers(): CurrentUser[] {
    return [
      {
        user_id: 'demo_001',
        user_name: '김철수',
        seat_number: 12,
        check_in_time: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        elapsed_time_minutes: 120,
        membership_type: '정기권',
        remaining_hours: 5,
        status: 'studying',
      },
      {
        user_id: 'demo_002',
        user_name: '이영희',
        seat_number: 7,
        check_in_time: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
        elapsed_time_minutes: 60,
        membership_type: '일일권',
        status: 'on_break',
      },
    ];
  }

  /**
   * 폴백: 개발용 일일 출결 더미 데이터
   */
  private getFallbackDailyRecords(date: string): DailyAttendanceRecord[] {
    return [
      {
        user_id: 'demo_001',
        user_name: '김철수',
        seat_number: 12,
        check_in_time: new Date(date + 'T09:00:00').toISOString(),
        check_out_time: new Date(date + 'T15:30:00').toISOString(),
        total_minutes: 390,
        membership_type: '정기권',
        status: 'completed',
      },
      {
        user_id: 'demo_002',
        user_name: '이영희',
        seat_number: 7,
        check_in_time: new Date(date + 'T10:30:00').toISOString(),
        total_minutes: 180,
        membership_type: '일일권',
        status: 'active',
      },
    ];
  }

  /**
   * 폴백: 개발용 통계 더미 데이터
   */
  private getFallbackStats(): AttendanceStats {
    return {
      total_users: 45,
      current_users: 28,
      today_checkins: 32,
      average_session_minutes: 185,
      peak_hours: [
        { hour: 14, user_count: 35, hour_display: '14시' },
        { hour: 19, user_count: 30, hour_display: '19시' },
        { hour: 21, user_count: 28, hour_display: '21시' },
      ],
      membership_breakdown: {
        정기권: 18,
        일일권: 7,
        시간권: 3,
      },
      last_updated: new Date().toISOString(),
    };
  }
}
